// @flow

import { type UserInputValidationError, type UserInputValidatorArgs, onlyWhenSupport } from '@tectonics/prs/validation';

import { validateCardHolderName } from './card-holder-name';
import { validateCardNumber } from './card-number';
import { validateCardExpiryDate } from './card-expiry-date';
import { validateCardSecurityCode } from './card-security-code';
import { validateAcceptVisaTerms } from './accept-visa-terms';
import { getCurrentFeatures } from '@tectonics/prs/payment-spec';
import type { CardBrand } from '@tectonics/prs/card-brand';
import { detectCardBrand } from '@tectonics/prs/card-brand';
import type { UserInputValues } from '@tectonics/prs/user-input-values';
import type { IncomingPayment } from '@tectonics/prs/incoming-payment';

export function validator({
  spec,
  prsSupport,
  userInputName,
  userInputValue,
  userInputValues,
  incomingPayment,
}: UserInputValidatorArgs): ?UserInputValidationError {
  const currentFeatures = getCurrentFeatures(spec, userInputValues['selectedPaymentMethod']);
  const onlyWhen = onlyWhenSupport(currentFeatures, prsSupport);
  const isNotOneClickCreditCard = !isOneClickCreditCardPaymentMethod(userInputValues) || undefined;
  const cardBrand = resolveCardBrand(incomingPayment, userInputValues);

  switch (userInputName) {
    case 'cardHolderName':
      return onlyWhen('field_card_holder_name') && isNotOneClickCreditCard && validateCardHolderName(userInputValue);

    case 'cardNumber':
      return onlyWhen('field_card_number') && isNotOneClickCreditCard && validateCardNumber(userInputValue);

    case 'cardExpiryDate':
      return onlyWhen('field_card_expiry_date') && isNotOneClickCreditCard && validateCardExpiryDate(userInputValue);

    case 'cardSecurityCode':
      return onlyWhen('field_card_security_code') && validateCardSecurityCode(userInputValue, cardBrand);

    case 'acceptVisaTerms':
      return onlyWhen('field_accept_visa_terms') && validateAcceptVisaTerms(userInputValue, cardBrand);

    default:
      return undefined;
  }
}

function resolveCardBrand(incomingPayment: IncomingPayment, userInputValues: UserInputValues): ?CardBrand {
  if (isOneClickCreditCardPaymentMethod(userInputValues)) {
    const [oneClickCard] = incomingPayment.one_click || [];
    return oneClickCard && oneClickCard.brand;
  }

  return detectCardBrand(userInputValues['cardNumber']);
}

function isOneClickCreditCardPaymentMethod(userInputValues: UserInputValues): boolean {
  const selectedPaymentMethod = userInputValues['selectedPaymentMethod'];
  const selectedCardType = userInputValues['selectedCardType'];

  return selectedPaymentMethod === 'creditcard' && selectedCardType === 'oneclick';
}
